/**
 * Marketing-surface design tokens — DELIBERATELY SEPARATE from the "Calm Fintech" app tokens in
 * `../design-tokens.ts`.
 *
 * FLAGGED DECISION (session, 2026-07-26): the approved landing prototype
 * (`valubrieflanding_2.html`) uses a warm green marketing palette (`#1E7A3D` primary) that does
 * NOT match the app's Calm Fintech tokens (navy accent `#1E3A8A`, verified-green `#166534`). This
 * is the SAME kind of "one hex value, two names" drift the Calm Fintech file itself was built to
 * kill — so rather than silently blending the two palettes (which would reintroduce exactly that
 * drift one layer up), this file keeps the marketing palette in its own, clearly-scoped module:
 *
 *   - `../design-tokens.ts`  -> the PRODUCT surface (ResultScreen, ProvenanceChip, confirm grid,
 *     reports). Trust-chip colors carry PROVENANCE MEANING (verified/indicative/gap/...) — they are
 *     semantic, not decorative, and must never be reskinned for brand mood.
 *   - `marketing/tokens.ts` (this file) -> the MARKETING surface only (landing page). Colors here
 *     carry BRAND meaning, not provenance meaning — the landing page has its own green because
 *     that's the approved brand mark, not because green means "verified" here.
 *
 * Reverse this by: if/when the Design-System owner ratifies ONE palette for both surfaces, delete
 * this file and re-point `marketing/*` imports at `../design-tokens.ts`. Until then, keeping them
 * separate is the conservative choice — it cannot cause the app's trust-chip colors to drift.
 *
 * Values copied verbatim from `valubrieflanding_2.html`'s `:root` block (the approved prototype).
 */

export const marketingColors = {
  green: "#1E7A3D",
  greenDeep: "#14532D",
  greenSoft: "#E8F3EC",
  greenBorder: "#CFE4D6",
  ink: "#152219",
  slate: "#5B6B60",
  paper: "#FCFCFA",
  white: "#FFFFFF",
  rule: "#E4E8E2",
} as const;

/** rgb triplets (no `rgb()` wrapper) for canvas `rgba()` composition — mirrors the implementation
 * guide's `--canvas-*` custom-property convention (§3) so a future brand tweak only touches this
 * file, never the animation engine.
 *
 * KEPT AS THE LIGHT SCHEME'S NAME (A-MARKETING, 2026-09-01). `canvasPaletteFor()` below is what the
 * engine reads now; these three values are unchanged and are the light palette's node/label/halo,
 * so any consumer still importing this gets exactly what it got before. */
export const canvasColorTriplets = {
  node: "30, 122, 61", // marketingColors.green as r,g,b
  label: "20, 83, 45", // a darker green for label text (matches prototype's softText color)
  halo: "252, 252, 250", // marketingColors.paper as r,g,b — the label halo/background
} as const;

/**
 * THE HERO DRAWING'S PALETTE, IN TWO SCHEMES (A-MARKETING, 2026-09-01).
 *
 * WHY THIS EXISTS. Dark mode shipped on 2026-09-01 and the hero canvas did not get it. Thirteen
 * colours were written into `HeroCanvas.engine.ts` as literals: the map's ground is `#F7F8F5`, the
 * parcels are `#F2F4EF`/`#F4F5F1`, and the street grid is `rgba(21,34,25,…)`. Painted on a dark
 * page that is inside-out — near-white blocks scattered over a dark hero, dark linework invisible
 * against it, and the arterial centre-lines (white at 0.7) reading as bright dashes.
 *
 * A CSS `filter: invert()` on the canvas would have been one line and was rejected: it inverts the
 * greens too, and this page's green is the brand mark, not decoration.
 *
 * THE SPLIT. `ink` and `parcel*` swap ends — dark linework on a light ground becomes light linework
 * on a dark ground. `node` and `label` follow the brand tokens app.css already defines for each
 * scheme (`--vb-brand-green` #1E7A3D / #6FCB92, and #14532D / #9FD5B4 for label text). `halo` is
 * whichever ground the labels sit on, because its whole job is to knock a hole behind the type.
 * `water` does NOT swap: a muted blue at 0.13–0.16 alpha reads as a river against either ground,
 * and swapping it would only make the one non-brand hue on the page harder to keep track of.
 *
 * NONE OF THESE CARRY MEANING. This is an illustration, not a chart — `HeroCanvas.engine.ts`'s own
 * deviation note is what keeps it honest: with no comps loaded the engine draws the geometry and
 * omits every number. Nothing here needs a contrast ratio because nothing here is information;
 * the drawing's one accessible affordance is the `aria-label` on the canvas element.
 */
export type CanvasPalette = {
  /** rgb triplet — the brand green, used for nodes, links and parcel highlights. */
  node: string;
  /** rgb triplet — label type drawn over the map. */
  label: string;
  /** rgb triplet — the halo knocked out behind label type. Always the ground. */
  halo: string;
  /** rgb triplet — the map's linework: streets, arteries, block edges, place names. */
  ink: string;
  /** solid hex — the map's ground. */
  ground: string;
  /** solid hex — the two alternating block fills. */
  parcelA: string;
  parcelB: string;
  /** rgb triplets — the river, two passes. */
  water: string;
  waterLight: string;
  /** full rgba — the dashed centre line down an artery, drawn over `ink`. */
  dash: string;
  /** solid hex — the pinned subject marker. */
  pin: string;
};

export const canvasPalettes: { light: CanvasPalette; dark: CanvasPalette } = {
  light: {
    node: "30, 122, 61",
    label: "20, 83, 45",
    halo: "252, 252, 250",
    ink: "21, 34, 25",
    ground: "#F7F8F5",
    parcelA: "#F2F4EF",
    parcelB: "#F4F5F1",
    water: "122, 158, 178",
    waterLight: "139, 176, 196",
    dash: "rgba(255,255,255,0.7)",
    pin: "#1E7A3D",
  },
  dark: {
    node: "111, 203, 146", //   --vb-brand-green      dark  #6FCB92
    label: "159, 213, 180", //  --vb-brand-green-deep dark  #9FD5B4
    halo: "14, 21, 18", //      --vb-brand-paper      dark  #0E1512
    ink: "230, 239, 233", //    --vb-brand-ink        dark  #E6EFE9
    ground: "#121A16", //       one step off the dark paper, the way #F7F8F5 is off #FCFCFA
    parcelA: "#161F1A", //      --vb-brand-white      dark
    parcelB: "#18221C",
    water: "122, 158, 178",
    waterLight: "139, 176, 196",
    dash: "rgba(14,21,18,0.7)", // the ground, because the artery under it is now light
    pin: "#6FCB92",
  },
};

export function canvasPaletteFor(dark: boolean): CanvasPalette {
  return dark ? canvasPalettes.dark : canvasPalettes.light;
}

export const marketingType = {
  fontSans: "'Inter', system-ui, sans-serif",
  fontMono: "'IBM Plex Mono', ui-monospace, monospace",
} as const;

export const marketingLayout = {
  gutter: "clamp(20px, 5vw, 64px)",
  maxWidth: "1160px",
} as const;
